import type { BumperPart, GuideRailPart, PlungerLanePart, TableDefinition, TargetPart } from './definition';

const VISUAL_ONLY_PINCH_RAILS = [
  'left-orbit-outer',
  'left-orbit-inner',
  'top-divider-left',
  'top-divider-right',
] as const;

function shapeGrandFinishChicane(table: TableDefinition): void {
  if (table.id !== 'grand-circuit-v1') return;

  const placements: Record<string, { x: number; y: number }> = {
    'target-1': { x: 0.31, y: 0.53 },
    'target-2': { x: 0.47, y: 0.485 },
    'target-3': { x: 0.63, y: 0.55 },
  };

  for (const [id, placement] of Object.entries(placements)) {
    const target = table.parts.find(
      (part): part is TargetPart => part.id === id && part.kind === 'target',
    );
    if (!target) continue;
    target.center = {
      x: table.board.width * placement.x,
      y: table.board.height * placement.y,
    };
  }

  const finish = table.rules?.missions?.find((mission) => mission.id === 'gc-targets');
  if (finish) {
    finish.name = 'Clear the Chicane';
    finish.description = 'Drop all three staggered finish-line sectors.';
    finish.partIds = ['target-1', 'target-2', 'target-3'];
    finish.count = 3;
    finish.bonus = 12000;
  }
}

function openClockworkTargetBank(table: TableDefinition): void {
  if (table.id !== 'clockwork-district-v1') return;

  const placements: Record<string, { x: number; y: number; width: number }> = {
    'target-1': { x: 0.28, y: 0.5, width: 2.75 },
    'target-2': { x: 0.43, y: 0.46, width: 2.6 },
    'target-3': { x: 0.7, y: 0.54, width: 2.75 },
  };

  for (const [id, placement] of Object.entries(placements)) {
    const target = table.parts.find(
      (part): part is TargetPart => part.id === id && part.kind === 'target',
    );
    if (!target) continue;
    target.center = {
      x: table.board.width * placement.x,
      y: table.board.height * placement.y,
    };
    target.width = placement.width;
  }
}

function tuneClockworkGovernor(table: TableDefinition): void {
  if (table.id !== 'clockwork-district-v1') return;

  const specs: Record<string, Pick<BumperPart, 'impulse' | 'scoreValue'>> = {
    'bumper-left': { impulse: 56, scoreValue: 700 },
    'bumper-center': { impulse: 80, scoreValue: 1450 },
    'bumper-right': { impulse: 67, scoreValue: 950 },
  };

  for (const [id, spec] of Object.entries(specs)) {
    const bumper = table.parts.find(
      (part): part is BumperPart => part.id === id && part.kind === 'bumper',
    );
    if (!bumper) continue;
    bumper.impulse = spec.impulse;
    bumper.scoreValue = spec.scoreValue;
  }
}

function tuneCosmicMeteorField(table: TableDefinition): void {
  if (table.id !== 'cosmic-relay-v1') return;

  const specs: Record<string, Pick<BumperPart, 'radius' | 'impulse' | 'scoreValue'>> = {
    'bumper-left': { radius: 3.65, impulse: 46, scoreValue: 800 },
    'bumper-center': { radius: 2.7, impulse: 82, scoreValue: 1700 },
    'bumper-right': { radius: 3, impulse: 68, scoreValue: 1150 },
  };

  // Meteor Triangulation already requires all three contacts. Make each contact
  // physically matter: the left gravity well is broad and controllable, the
  // center meteor is a compact violent hazard, and the right comet is the fast
  // middle-ground transfer. The objective now changes rebound strategy instead
  // of asking for three nearly interchangeable pop hits.
  for (const [id, spec] of Object.entries(specs)) {
    const meteor = table.parts.find(
      (part): part is BumperPart => part.id === id && part.kind === 'bumper',
    );
    if (!meteor) continue;
    meteor.radius = spec.radius;
    meteor.impulse = spec.impulse;
    meteor.scoreValue = spec.scoreValue;
  }
}

function tuneDeepSeaReefCurrents(table: TableDefinition): void {
  if (table.id !== 'deep-sea-dive-v1') return;

  const specs: Record<string, Pick<BumperPart, 'impulse' | 'scoreValue'>> = {
    'bumper-left': { impulse: 44, scoreValue: 650 },
    'bumper-center': { impulse: 70, scoreValue: 1500 },
    'bumper-right': { impulse: 57, scoreValue: 950 },
  };

  for (const [id, spec] of Object.entries(specs)) {
    const bumper = table.parts.find(
      (part): part is BumperPart => part.id === id && part.kind === 'bumper',
    );
    if (!bumper) continue;
    bumper.impulse = spec.impulse;
    bumper.scoreValue = spec.scoreValue;
  }
}

function addDeepSeaShooterReturnFunnel(table: TableDefinition): void {
  if (table.id !== 'deep-sea-dive-v1') return;
  if (table.parts.some((part) => part.id === 'deep-sea-shooter-return-funnel')) return;

  // Deep Sea deliberately runs forgiving, lower-energy recovery currents. That
  // makes a quiet shelf beside the shooter divider especially sticky. This broad
  // angled current rail keeps the launch lane open while feeding weak rebounds
  // down-left into the return-lane game instead of letting them park on the wall.
  table.parts.push({
    id: 'deep-sea-shooter-return-funnel',
    kind: 'guide-rail',
    scoreValue: 0,
    thickness: 1,
    material: 'metal',
    color: table.theme.accentColor,
    points: [
      { x: table.board.width * 0.865, y: table.board.height * 0.64 },
      { x: table.board.width * 0.765, y: table.board.height * 0.73 },
    ],
  });
}

function addCosmicShooterReturnFunnel(table: TableDefinition): void {
  if (table.id !== 'cosmic-relay-v1') return;
  if (table.parts.some((part) => part.id === 'cosmic-shooter-return-funnel')) return;

  // Cosmic's larger cabinet leaves a quiet pocket between the lower end of the
  // shooter divider and the right sling. A short angled return rail closes that
  // shelf without sealing the shooter lane, sending slow balls down-left toward
  // the active lower playfield instead of letting them park beside the divider.
  table.parts.push({
    id: 'cosmic-shooter-return-funnel',
    kind: 'guide-rail',
    scoreValue: 0,
    thickness: 0.9,
    material: 'metal',
    color: '#8fb8d8',
    points: [
      { x: table.board.width * 0.865, y: table.board.height * 0.65 },
      { x: table.board.width * 0.775, y: table.board.height * 0.72 },
    ],
  });
}

function openCosmicUpperShooterReturn(table: TableDefinition): void {
  if (table.id !== 'cosmic-relay-v1') return;
  const lane = table.parts.find(
    (part): part is PlungerLanePart => part.kind === 'plunger-lane',
  );
  if (!lane?.divider || lane.divider.points.length < 2) return;

  // Cosmic's orbital ramp returns immediately beside the long shooter divider.
  // Keeping that divider all the way into the upper third creates a narrow shelf
  // where gravity can pin a slow ball. The launch itself is carried by the
  // authored scripted path, so open the upper return and retain the divider from
  // the mid-playfield down where it still protects the physical shooter lane.
  lane.divider.points[0] = {
    x: lane.divider.points[0]!.x,
    y: table.board.height * 0.38,
  };
}

function addHauntedShooterReturnFunnel(table: TableDefinition): void {
  if (table.id !== 'haunted-keep-v1') return;
  if (table.parts.some((part) => part.id === 'haunted-shooter-return-funnel')) return;

  // Haunted is the widest/tallest upper-battle layout and leaves a long quiet
  // shelf between the shooter divider and the right sling. This angled iron
  // return keeps that lane open for launches while steering weak rebounds back
  // down-left into the active lower playfield instead of a dead right pocket.
  table.parts.push({
    id: 'haunted-shooter-return-funnel',
    kind: 'guide-rail',
    scoreValue: 0,
    thickness: 0.95,
    material: 'metal',
    color: '#8d929c',
    points: [
      { x: table.board.width * 0.865, y: table.board.height * 0.65 },
      { x: table.board.width * 0.775, y: table.board.height * 0.72 },
    ],
  });
}

function addNeonShooterReturnFunnel(table: TableDefinition): void {
  if (table.id !== 'neon-rush-v1') return;
  if (table.parts.some((part) => part.id === 'neon-shooter-return-funnel')) return;

  // Neon is intentionally the fast, low-nudge machine, so a quiet shelf beside
  // the shooter lane is especially punitive. Keep the lane itself open, but add
  // a shallow angled conductor rail that redirects weak right-side rebounds
  // down-left toward the slings/flippers instead of letting them park by the wall.
  table.parts.push({
    id: 'neon-shooter-return-funnel',
    kind: 'guide-rail',
    scoreValue: 0,
    thickness: 0.85,
    material: 'metal',
    color: table.theme.accentColor,
    points: [
      { x: table.board.width * 0.865, y: table.board.height * 0.65 },
      { x: table.board.width * 0.79, y: table.board.height * 0.715 },
    ],
  });
}

function addCandyShooterReturnFunnel(table: TableDefinition): void {
  if (table.id !== 'candy-carnival-v1') return;
  if (table.parts.some((part) => part.id === 'candy-shooter-return-funnel')) return;

  // Candy runs at the gentlest pitch of the cloned cabinets, so weak returns can
  // linger beside the shooter divider longer than on the faster machines. Add a
  // broad candy-slide rail that keeps the shooter lane open while feeding those
  // slow rebounds down-left toward the active sling/flipper area.
  table.parts.push({
    id: 'candy-shooter-return-funnel',
    kind: 'guide-rail',
    scoreValue: 0,
    thickness: 0.95,
    material: 'metal',
    color: table.theme.accentColor,
    points: [
      { x: table.board.width * 0.865, y: table.board.height * 0.645 },
      { x: table.board.width * 0.77, y: table.board.height * 0.725 },
    ],
  });
}

function addJungleShooterReturnFunnel(table: TableDefinition): void {
  if (table.id !== 'jungle-temple-v1') return;
  if (table.parts.some((part) => part.id === 'jungle-shooter-return-funnel')) return;

  // Jungle's broad recovery layout still inherited the quiet shelf beside the
  // shooter divider. Shape that space as a physical vine chute so weak right-side
  // rebounds are guided down-left toward the active lower playfield instead of
  // settling against the lane wall, without closing off the scripted launch path.
  table.parts.push({
    id: 'jungle-shooter-return-funnel',
    kind: 'guide-rail',
    scoreValue: 0,
    thickness: 0.95,
    material: 'wood',
    color: table.theme.accentColor,
    points: [
      { x: table.board.width * 0.865, y: table.board.height * 0.645 },
      { x: table.board.width * 0.775, y: table.board.height * 0.725 },
    ],
  });
}

function addClockworkShooterReturnFunnel(table: TableDefinition): void {
  if (table.id !== 'clockwork-district-v1') return;
  if (table.parts.some((part) => part.id === 'clockwork-shooter-return-funnel')) return;

  // Clockwork's sharp rebounds and tall cabinet can send a spent ball back into
  // the quiet shelf beside the shooter divider. A compact gear-guide rail keeps
  // that rebound moving down-left into the lower mechanism instead of allowing a
  // stationary wall catch, while leaving the scripted shooter path unobstructed.
  table.parts.push({
    id: 'clockwork-shooter-return-funnel',
    kind: 'guide-rail',
    scoreValue: 0,
    thickness: 0.9,
    material: 'metal',
    color: table.theme.secondaryColor ?? table.theme.accentColor,
    points: [
      { x: table.board.width * 0.865, y: table.board.height * 0.65 },
      { x: table.board.width * 0.785, y: table.board.height * 0.72 },
    ],
  });
}

function addFrontierShooterReturnFunnel(table: TableDefinition): void {
  if (table.id !== 'frontier-run-v1') return;
  if (table.parts.some((part) => part.id === 'frontier-shooter-return-funnel')) return;

  // Frontier's railway routes already provide its shot identity. This wooden
  // cattle-guard return is strictly recovery hardware: weak right-side rebounds
  // are sent down-left into the active lower chase instead of parking beside the
  // shooter divider, while the scripted launch lane remains completely open.
  table.parts.push({
    id: 'frontier-shooter-return-funnel',
    kind: 'guide-rail',
    scoreValue: 0,
    thickness: 0.95,
    material: 'wood',
    color: table.theme.secondaryColor ?? table.theme.accentColor,
    points: [
      { x: table.board.width * 0.865, y: table.board.height * 0.65 },
      { x: table.board.width * 0.78, y: table.board.height * 0.725 },
    ],
  });
}

function addDragonShooterReturnFunnel(table: TableDefinition): void {
  if (table.id !== 'dragon-keep-v1') return;
  if (table.parts.some((part) => part.id === 'dragon-shooter-return-funnel')) return;

  // Dragon keeps its fast boss-table pitch; this battlement rail only removes
  // the inherited shelf beside the shooter divider and feeds weak rebounds back
  // down-left into the four-flipper battle zone without obstructing the launch.
  table.parts.push({
    id: 'dragon-shooter-return-funnel',
    kind: 'guide-rail',
    scoreValue: 0,
    thickness: 1,
    material: 'metal',
    color: table.theme.secondaryColor ?? table.theme.accentColor,
    points: [
      { x: table.board.width * 0.865, y: table.board.height * 0.65 },
      { x: table.board.width * 0.785, y: table.board.height * 0.715 },
    ],
  });
}

function retargetCandyFrenzy(table: TableDefinition): void {
  if (table.id !== 'candy-carnival-v1') return;
  const mission = table.rules?.missions?.find((candidate) => candidate.id === 'candy-pops');
  if (!mission) return;

  mission.name = 'Sugar Rush Circuit';
  mission.description = 'Reload through all three Sugar Rush lanes.';
  mission.trigger = 'rollover';
  mission.partIds = ['roll-a', 'roll-b', 'roll-c'];
  mission.count = 3;
  mission.bonus = 6500;
}

function retargetCosmicMeteorStorm(table: TableDefinition): void {
  if (table.id !== 'cosmic-relay-v1') return;
  const mission = table.rules?.missions?.find((candidate) => candidate.id === 'cosmic-pops');
  if (!mission) return;

  mission.name = 'Meteor Triangulation';
  mission.description = 'Tag all three meteors around the orbital deflectors.';
  mission.partIds = ['bumper-left', 'bumper-center', 'bumper-right'];
  mission.count = 3;
  mission.bonus = 8500;
}

function retargetDeepSeaReefRescue(table: TableDefinition): void {
  if (table.id !== 'deep-sea-dive-v1') return;
  const mission = table.rules?.missions?.find((candidate) => candidate.id === 'deep-pops');
  if (!mission) return;

  // Deep Sea already owns two long 2x lower recovery currents. Make the old
  // ten-pop grind depend on saving the ball through both returns instead: this
  // rewards controlled recovery on the forgiving table and gives the lower
  // playfield a real objective before the decompression catch opens the lock.
  mission.name = 'Ride the Undertow';
  mission.description = 'Recover through both undertow return lanes.';
  mission.trigger = 'rollover';
  mission.partIds = ['return-roll-left', 'return-roll-right'];
  mission.count = 2;
  mission.bonus = 8000;
}

/**
 * Keep authored rail shapes visible while removing narrow wall/rail and
 * rail/rail channels that can capture a slow ball. Clockwork also uses an
 * offset, narrower target bank so its destructible robot target cannot become
 * a centered horizontal parking ledge under gravity. This post-build pass also
 * carries tightly scoped official-table gameplay identity corrections.
 */
export function openOfficialPinchRails(table: TableDefinition): TableDefinition {
  for (const id of VISUAL_ONLY_PINCH_RAILS) {
    const rail = table.parts.find(
      (part): part is GuideRailPart => part.id === id && part.kind === 'guide-rail',
    );
    if (rail) rail.collider = false;
  }
  shapeGrandFinishChicane(table);
  openClockworkTargetBank(table);
  tuneClockworkGovernor(table);
  tuneCosmicMeteorField(table);
  tuneDeepSeaReefCurrents(table);
  addDeepSeaShooterReturnFunnel(table);
  addCosmicShooterReturnFunnel(table);
  openCosmicUpperShooterReturn(table);
  addHauntedShooterReturnFunnel(table);
  addNeonShooterReturnFunnel(table);
  addCandyShooterReturnFunnel(table);
  addJungleShooterReturnFunnel(table);
  addClockworkShooterReturnFunnel(table);
  addFrontierShooterReturnFunnel(table);
  addDragonShooterReturnFunnel(table);
  retargetCandyFrenzy(table);
  retargetCosmicMeteorStorm(table);
  retargetDeepSeaReefRescue(table);
  return table;
}